(() => {
  if (customElements.get('bb-site-search')) return;

  class BBSiteSearch extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.input = this.querySelector('[data-bb-search-input]');
      this.panel = this.querySelector('[data-bb-search-panel]');
      this.directory = this.querySelector('[data-bb-team-directory]');
      this.products = this.querySelector('[data-bb-search-products]');
      this.results = this.querySelector('[data-bb-search-results]');
      this.status = this.querySelector('[data-bb-search-status]');
      this.allResults = this.querySelector('[data-bb-search-all]');
      this.clearButton = this.querySelector('[data-bb-search-clear]');
      this.events = new AbortController();
      this.cache = new Map();
      this.version = 0;
      const options = { signal: this.events.signal };

      this.input.addEventListener('focus', () => {
        if (this.returningFocus) return;
        this.open();
        this.update();
      }, options);
      this.input.addEventListener('click', () => {
        if (!this.hasAttribute('open')) {
          this.open();
          this.update();
        }
      }, options);
      this.input.addEventListener('input', () => {
        this.open();
        this.update();
      }, options);
      this.input.addEventListener('search', () => this.update(), options);
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.open();
        this.update();
        this.input.focus();
      }, options);
      this.querySelector('[data-bb-search-close]').addEventListener('click', () => this.close(true), options);
      this.querySelector('form').addEventListener('submit', (event) => {
        if (!this.input.value.trim()) {
          event.preventDefault();
          this.open();
          this.input.focus();
        }
      }, options);
      this.addEventListener('keydown', (event) => this.handleKey(event), options);
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.close();
      }, options);
      document.addEventListener('focusin', (event) => {
        if (!this.contains(event.target)) this.close();
      }, options);
      document.addEventListener('bb:school-menu-open', () => this.close(), options);
      window.addEventListener('resize', () => this.fitPanel(), options);
      window.addEventListener('scroll', () => this.fitPanel(), { ...options, passive: true });
      window.visualViewport?.addEventListener('resize', () => this.fitPanel(), options);
      this.clearButton.hidden = !this.input.value;
    }

    open() {
      if (this.hasAttribute('open')) return;
      document.querySelectorAll('bb-site-search[open]').forEach((other) => {
        if (other !== this) other.close();
      });
      document.querySelectorAll('[data-bb-nav-details][open]').forEach((menu) => { menu.open = false; });
      this.setAttribute('open', '');
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.fitPanel();
    }

    close(returnFocus = false) {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      if (returnFocus) {
        this.returningFocus = true;
        this.input.focus();
        this.returningFocus = false;
      }
    }

    fitPanel() {
      if (!this.hasAttribute('open')) return;
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
      const room = Math.max(180, bottom - this.panel.getBoundingClientRect().top - 16);
      this.style.setProperty('--bb-search-room', room + 'px');
    }

    update() {
      const query = this.input.value.trim();
      this.version += 1;
      const version = this.version;
      clearTimeout(this.timer);
      this.request?.abort();
      this.clearButton.hidden = !query;
      this.directory.dispatchEvent(new CustomEvent('bb:school-query', { detail: query }));
      this.products.hidden = !query;
      this.allResults.hidden = !query;
      this.results.replaceChildren();
      this.status.textContent = '';
      this.products.removeAttribute('aria-busy');
      const fullURL = new URL(this.dataset.searchUrl, window.location.origin);
      fullURL.search = new URLSearchParams({ q: query, type: 'product', 'options[prefix]': 'last' }).toString();
      this.allResults.href = fullURL.href;
      if (!query) return;
      this.status.textContent = this.dataset.loading;
      this.products.setAttribute('aria-busy', 'true');
      this.timer = setTimeout(() => this.fetchProducts(query, version), 220);
    }

    async fetchProducts(query, version) {
      try {
        let markup = this.cache.get(query);
        if (!markup) {
          this.request = new AbortController();
          const url = new URL(this.dataset.suggestUrl, window.location.origin);
          url.search = new URLSearchParams({
            q: query,
            'resources[type]': 'product',
            'resources[limit]': '6',
            'resources[options][unavailable_products]': 'last',
            section_id: 'bb-search-suggestions'
          }).toString();
          const response = await fetch(url, { signal: this.request.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Search unavailable');
          markup = await response.text();
          this.cache.set(query, markup);
          if (this.cache.size > 20) this.cache.delete(this.cache.keys().next().value);
        }
        if (version !== this.version) return;
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const suggestions = parsed.querySelector('[data-bb-product-suggestions]');
        if (!suggestions) throw new Error('Search response unavailable');
        this.results.replaceChildren(...[...suggestions.childNodes].map((node) => document.importNode(node, true)));
        this.status.textContent = '';
      } catch (error) {
        if (error.name === 'AbortError' || version !== this.version) return;
        this.status.textContent = this.dataset.error;
      } finally {
        if (version === this.version) this.products.removeAttribute('aria-busy');
      }
    }

    handleKey(event) {
      if (event.key === 'Escape' && this.hasAttribute('open')) {
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (event.target === this.input) {
        event.preventDefault();
        this.open();
        const links = this.visibleLinks();
        (event.key === 'ArrowDown' ? links[0] : links.at(-1))?.focus();
        return;
      }
      const links = this.visibleLinks();
      const current = links.indexOf(event.target);
      if (current === -1) return;
      event.preventDefault();
      const next = current + (event.key === 'ArrowDown' ? 1 : -1);
      if (next < 0 || next >= links.length) this.input.focus();
      else links[next].focus();
    }

    visibleLinks() {
      return [...this.panel.querySelectorAll('a')].filter((link) => !link.closest('[hidden]'));
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      clearTimeout(this.timer);
      this.events = null;
    }
  }

  customElements.define('bb-site-search', BBSiteSearch);
})();
